'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const Footer = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentYear, setCurrentYear] = useState('2026');

  useEffect(() => {
    setIsHydrated(true);
    setCurrentYear(new Date()?.getFullYear()?.toString());
  }, []);

  const footerLinks = {
    services: [
      { label: 'Find Mechanics', href: '/search-results' },
      { label: 'Compare Prices', href: '/price-compare' },
      { label: 'OEM Parts Finder', href: '/oem-parts-finder' },
      { label: 'DIY Guides', href: '/diy' }
    ],
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'For Mechanics', href: '/claim-listing' }
    ],
    support: [
      { label: 'Help Center', href: '/help' },
      { label: 'Contact Us', href: '/contact' },
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' }
    ]
  };

  const socialLinks = [
    { icon: 'facebook', href: '#', label: 'Facebook' },
    { icon: 'twitter', href: '#', label: 'Twitter' },
    { icon: 'instagram', href: '#', label: 'Instagram' },
    { icon: 'linkedin', href: '#', label: 'LinkedIn' }
  ];

  return (
    <footer className="bg-card border-t border-border">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary">
                <Icon name="WrenchScrewdriverIcon" size={24} className="text-primary-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="font-heading text-base font-semibold text-primary">
                  AutoFixIQ
                </span>
                <span className="font-caption text-xs text-muted-foreground">
                  Trusted Mechanics
                </span>
              </div>
            </div>
            <p className="font-body text-sm text-text-secondary leading-relaxed mb-4">
              Connecting BMW and Lexus owners nationwide with trusted independent mechanics since 2020.
            </p>
            <div className="flex items-center space-x-3">
              {socialLinks?.map((social) => (
                <a
                  key={social?.icon}
                  href={social?.href}
                  className="flex items-center justify-center w-9 h-9 rounded-full bg-muted text-text-secondary transition-smooth hover:bg-primary hover:text-primary-foreground"
                  aria-label={social?.label}
                >
                  <Icon name="ShareIcon" size={18} />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-heading text-sm font-semibold text-text-primary mb-4">
              Services
            </h3>
            <ul className="space-y-3">
              {footerLinks?.services?.map((link) => (
                <li key={link?.href}>
                  <Link
                    href={link?.href}
                    className="font-body text-sm text-text-secondary transition-smooth hover:text-primary"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-sm font-semibold text-text-primary mb-4">
              Company
            </h3>
            <ul className="space-y-3">
              {footerLinks?.company?.map((link) => (
                <li key={link?.href}>
                  <Link
                    href={link?.href}
                    className="font-body text-sm text-text-secondary transition-smooth hover:text-primary"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-heading text-sm font-semibold text-text-primary mb-4">
              Support
            </h3>
            <ul className="space-y-3">
              {footerLinks?.support?.map((link) => (
                <li key={link?.href}>
                  <Link
                    href={link?.href}
                    className="font-body text-sm text-text-secondary transition-smooth hover:text-primary"
                  >
                    {link?.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-border">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="font-body text-sm text-text-secondary text-center sm:text-left">
              {isHydrated ? `© ${currentYear}` : '© 2026'} AutoFixIQ. All rights reserved.
            </p>
            <div className="flex items-center space-x-6">
              <Link
                href="/privacy"
                className="font-body text-sm text-text-secondary transition-smooth hover:text-primary"
              >
                Privacy
              </Link>
              <Link
                href="/terms"
                className="font-body text-sm text-text-secondary transition-smooth hover:text-primary"
              >
                Terms
              </Link>
              <Link
                href="/cookies"
                className="font-body text-sm text-text-secondary transition-smooth hover:text-primary"
              >
                Cookies
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;